import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Button,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Image as ImageIcon,
  History as HistoryIcon,
  Bookmarks as BookmarksIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import ImageGenerationPanel from '../components/ImageGenerationPanel';
import { ImagePromptService } from '../services/imagePromptService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>{children}</Box>}
    </div>
  );
}

const ImageGenerationPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [tabValue, setTabValue] = useState(0);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [generationHistory, setGenerationHistory] = useState<any[]>([]);
  
  // Saved prompts state - both local and API
  const [localPrompts, setLocalPrompts] = useState<Array<{id: string, prompt: string, timestamp: Date, model?: string}>>([]);
  const [apiPrompts, setApiPrompts] = useState<Array<{promptId: string, prompt: string, createdAt: string, metadata?: any}>>([]);
  const [useApiStorage, setUseApiStorage] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Load API prompts when switching to saved prompts tab
    if (newValue === 1 && useApiStorage) {
      loadApiPrompts();
    }
  };

  // Load local saved prompts
  const loadLocalPrompts = () => {
    const saved = localStorage.getItem('image_generation_prompts');
    if (saved) {
      try {
        const prompts = JSON.parse(saved);
        setLocalPrompts(prompts.map((p: any) => ({
          ...p,
          timestamp: new Date(p.timestamp)
        })));
      } catch (err) {
        console.error('Error loading local prompts:', err);
      }
    }
  };

  // Load API prompts
  const loadApiPrompts = async () => {
    try {
      const response = await ImagePromptService.listPrompts();
      const prompts = Array.isArray(response) ? response : [];
      setApiPrompts(prompts);
    } catch (err) {
      console.error('Error loading API prompts:', err);
      setApiPrompts([]);
    }
  };

  // Delete local prompt
  const deleteLocalPrompt = (id: string) => {
    const updatedPrompts = localPrompts.filter(p => p.id !== id);
    setLocalPrompts(updatedPrompts);
    localStorage.setItem('image_generation_prompts', JSON.stringify(updatedPrompts));
  };

  // Export prompts
  const exportPrompts = () => {
    const dataStr = JSON.stringify(useApiStorage ? apiPrompts : localPrompts, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `image-generation-prompts-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  // Import prompts
  const importPrompts = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (useApiStorage) {
      try {
        await ImagePromptService.uploadPrompt(file);
        await loadApiPrompts();
      } catch (err) {
        console.error('Error uploading prompt:', err);
        setError('Failed to upload prompt file');
      }
    } else {
      try {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const imported = JSON.parse(e.target?.result as string);
            const validPrompts = imported.filter((p: any) => p.prompt && p.id);
            const mergedPrompts = [...localPrompts, ...validPrompts.map((p: any) => ({
              ...p,
              timestamp: new Date(p.timestamp),
              id: `imported-${Date.now()}-${p.id}`
            }))];
            setLocalPrompts(mergedPrompts);
            localStorage.setItem('image_generation_prompts', JSON.stringify(mergedPrompts));
          } catch (error) {
            setError('Failed to import prompts');
          }
        };
        reader.readAsText(file);
      } catch (err) {
        setError('Failed to read file');
      }
    }
  };

  useEffect(() => {
    loadLocalPrompts();
  }, []);

  return (
    <Container 
      maxWidth="md" 
      sx={{ 
        py: { xs: 2, sm: 3, md: 4 },
        px: { xs: 1.5, sm: 2, md: 3 },
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 1, 
            mb: 2,
            fontSize: { xs: '1.5rem', sm: '2.125rem' },
            flexDirection: { xs: 'column', sm: 'row' }
          }}
        >
          <ImageIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          {isMobile ? 'AI Image Generation Studio' : 'AI Image Generation Studio'}
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          Generate stunning images with AI across multiple models
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper 
        sx={{ mb: 3, width: '100%', overflow: 'hidden' }} 
        elevation={0}
      >
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="image generation tabs" 
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
        >
          <Tab 
            icon={<ImageIcon />} 
            label="Generate"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          />
          <Tab 
            icon={<BookmarksIcon />} 
            label="Saved Prompts"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          />
          <Tab 
            icon={<HistoryIcon />} 
            label="History"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          />
        </Tabs>
      </Paper>
      <TabPanel value={tabValue} index={0}>
        <ImageGenerationPanel 
          initialPrompt={currentPrompt}
          onPromptChange={(prompt) => {
            // Optional: Auto-save prompts
          }}
          onPromptSaved={() => {
            // Reload prompts when a new one is saved
            loadLocalPrompts();
            if (useApiStorage) {
              loadApiPrompts();
            }
          }}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' }, 
                justifyContent: 'space-between', 
                mb: 3,
                gap: { xs: 2, sm: 0 }
              }}>
                <Typography 
                  variant="h5"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                >
                  Saved Prompts ({useApiStorage ? apiPrompts.length : localPrompts.length})
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' }, 
                  gap: { xs: 1.5, sm: 2 },
                  width: { xs: '100%', sm: 'auto' }
                }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={useApiStorage}
                        onChange={(e) => {
                          setUseApiStorage(e.target.checked);
                          if (e.target.checked) {
                            loadApiPrompts();
                          }
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {useApiStorage ? <CloudIcon sx={{ fontSize: { xs: 20, sm: 24 } }} /> : <StorageIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />}
                        <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          {useApiStorage ? 'Cloud Storage' : 'Local Storage'}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
                    <Tooltip title="Import prompts">
                      <IconButton 
                        component="label"
                        sx={{ minHeight: 44, minWidth: 44 }}
                      >
                        <UploadIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                        <input
                          type="file"
                          hidden
                          accept=".json"
                          onChange={importPrompts}
                        />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Export prompts">
                      <IconButton 
                        onClick={exportPrompts} 
                        disabled={useApiStorage ? apiPrompts.length === 0 : localPrompts.length === 0}
                        sx={{ minHeight: 44, minWidth: 44 }}
                      >
                        <DownloadIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>

              {useApiStorage ? (
                // API Prompts
                apiPrompts.length === 0 ? (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                    No cloud-saved prompts yet. Generate some images and save your favorite prompts!
                  </Typography>
                ) : (
                  <Grid 
                    container 
                    spacing={2}
                    sx={{
                      width: '100%',
                      margin: 0,
                      '& > .MuiGrid-item': {
                        paddingLeft: 2
                      }
                    }}
                  >
                    {apiPrompts.map((apiPrompt) => (
                      <Grid item xs={12} md={6} key={apiPrompt.promptId}>
                        <Card variant="outlined">
                          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                mb: 2,
                                fontSize: { xs: '0.875rem', sm: '1rem' }
                              }}
                            >
                              {apiPrompt.prompt}
                            </Typography>
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: { xs: 'column', sm: 'row' },
                              justifyContent: 'space-between', 
                              alignItems: { xs: 'flex-start', sm: 'center' },
                              gap: { xs: 1.5, sm: 0 }
                            }}>
                              <Box>
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary"
                                  sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                >
                                  {new Date(apiPrompt.createdAt).toLocaleDateString()}
                                </Typography>
                                {apiPrompt.metadata?.model && (
                                  <Typography 
                                    variant="caption" 
                                    color="text.secondary" 
                                    sx={{ 
                                      ml: { xs: 0, sm: 2 },
                                      display: { xs: 'block', sm: 'inline' },
                                      fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                    }}
                                  >
                                    Model: {apiPrompt.metadata.model}
                                  </Typography>
                                )}
                              </Box>
                              <Box>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  fullWidth={isMobile}
                                  onClick={() => {
                                    setCurrentPrompt(apiPrompt.prompt);
                                    setTabValue(0);
                                  }}
                                  sx={{ 
                                    minHeight: 36,
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                  }}
                                >
                                  Use
                                </Button>
                                <Tooltip title="Download">
                                  <IconButton
                                    size="small"
                                    onClick={() => ImagePromptService.downloadPrompt(apiPrompt.promptId)}
                                    sx={{ minHeight: 36, minWidth: 36 }}
                                  >
                                    <DownloadIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )
              ) : (
                // Local Prompts
                localPrompts.length === 0 ? (
                  <Typography 
                    color="text.secondary" 
                    align="center" 
                    sx={{ 
                      py: 4,
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    No locally saved prompts yet. Generate some images and save your favorite prompts!
                  </Typography>
                ) : (
                  <Grid 
                    container 
                    spacing={2}
                    sx={{
                      width: '100%',
                      margin: 0,
                      '& > .MuiGrid-item': {
                        paddingLeft: 2
                      }
                    }}
                  >
                    {localPrompts.map((localPrompt) => (
                      <Grid item xs={12} md={6} key={localPrompt.id}>
                        <Card variant="outlined">
                          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                            <Typography variant="body1" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              {localPrompt.prompt}
                            </Typography>
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: { xs: 'column', sm: 'row' },
                              justifyContent: 'space-between', 
                              alignItems: { xs: 'flex-start', sm: 'center' },
                              gap: { xs: 1.5, sm: 0 }
                            }}>
                              <Box>
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary"
                                  sx={{ 
                                    fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                  }}
                                >
                                  {localPrompt.timestamp.toLocaleDateString()}
                                </Typography>
                                {localPrompt.model && (
                                  <Typography 
                                    variant="caption" 
                                    color="text.secondary" 
                                    sx={{ 
                                      ml: { xs: 0, sm: 2 },
                                      display: { xs: 'block', sm: 'inline' },
                                      fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                    }}
                                  >
                                    Model: {localPrompt.model}
                                  </Typography>
                                )}
                              </Box>
                              <Box sx={{ 
                                display: 'flex', 
                                gap: 1,
                                width: { xs: '100%', sm: 'auto' }
                              }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  fullWidth={isMobile}
                                  onClick={() => {
                                    setCurrentPrompt(localPrompt.prompt);
                                    setTabValue(0);
                                  }}
                                  sx={{ 
                                    minHeight: 36,
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                  }}
                                >
                                  Use
                                </Button>
                                <IconButton
                                  size="small"
                                  onClick={() => deleteLocalPrompt(localPrompt.id)}
                                  sx={{ minHeight: 36, minWidth: 36 }}
                                >
                                  <DeleteIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                </IconButton>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )
              )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Typography 
          variant="h5" 
          gutterBottom
          sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
        >
          Generation History
        </Typography>
        <Typography 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          History feature coming soon! Your recent generations will appear here.
        </Typography>
      </TabPanel>
    </Container>
    );
}

export default ImageGenerationPage;